<template>
  <div class="image-model-seo-page" :class="{ 'image-model-seo-page--midjourney': i18nKey === 'midjourney' }">
    <div class="container">
      <div class="seo-first-screen">
        <nav class="breadcrumb" aria-label="Breadcrumb">
          <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
          <span>&rsaquo;</span>
          <router-link to="/models">{{ $t('footer.aiImages') }}</router-link>
          <span>&rsaquo;</span>
          <span>{{ modelName }}</span>
        </nav>

      <section class="seo-hero">
        <div class="seo-hero-copy">
          <h1 class="seo-hero-title">{{ $t('seo.imageModels.' + i18nKey + '.heroHeadline') }}</h1>
          <p class="seo-hero-intro">{{ $t('seo.imageModels.' + i18nKey + '.heroIntro') }}</p>
          <div class="seo-badges" aria-label="Model capabilities">
            <span v-for="badge in badges" :key="badge">{{ badge }}</span>
          </div>
        </div>

        <div class="seo-prompt-card">
          <div class="seo-prompt-heading">
            <div>
              <span class="seo-prompt-label">{{ $t('seo.imageModels.promptLabel') }}</span>
              <strong>{{ modelName }}</strong>
            </div>
            <span class="seo-credit-pill">{{ model ? model.credits : 7 }} credits</span>
          </div>
          <textarea
            class="seo-prompt-textarea"
            v-model="prompt"
            :placeholder="$t('home.generatorNew.promptPlaceholderImage')"
            rows="4"
            maxlength="2000"
            @keydown.enter.ctrl="goGenerate"
            @keydown.enter.meta="goGenerate"
          ></textarea>
          <div class="seo-prompt-footer">
            <span class="seo-prompt-hint">Ctrl / ⌘ + Enter</span>
            <div class="seo-prompt-actions">
              <span class="seo-prompt-count">{{ prompt.length }}/2000</span>
              <button class="seo-generate" @click="goGenerate" :disabled="!prompt.trim()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                <span>{{ $t('home.generate') }}</span>
              </button>
            </div>
          </div>
        </div>
      </section>
      </div>

      <flux1-seo-content
        v-if="i18nKey === 'flux1' || i18nKey === 'flux2' || i18nKey === 'stableDiffusion' || i18nKey === 'zImageTurbo'"
        :key="i18nKey"
        :i18n-key="i18nKey"
        :model-name="modelName"
        :features="features"
        :variants="variants"
        :use-cases="useCases"
        :faqs="faqs"
        :how-to-steps="howToSteps"
        @use-prompt="usePrompt"
        @back-to-generator="scrollToTop"
      />

      <seedream-seo-content
        v-else-if="i18nKey === 'seedream'"
        :model-name="modelName"
        :features="features"
        :variants="variants"
        :use-cases="useCases"
        :faqs="faqs"
        :how-to-steps="howToSteps"
        @use-prompt="usePrompt"
        @back-to-generator="scrollToTop"
      />

      <qwen-image-seo-content
        v-else-if="i18nKey === 'qwenImage2Pro'"
        :features="features"
        :use-cases="useCases"
        :faqs="faqs"
        :how-to-steps="howToSteps"
        @use-prompt="usePrompt"
        @back-to-generator="scrollToTop"
      />

      <gpt-image-seo-content
        v-else-if="i18nKey === 'gptImage'"
        :features="features"
        :variants="variants"
        :use-cases="useCases"
        :faqs="faqs"
        :how-to-steps="howToSteps"
        :related-models="relatedModels"
        @use-prompt="usePrompt"
        @back-to-generator="scrollToTop"
      />

      <template v-else>
      <section class="seo-section">
        <div class="seo-section-heading">
          <span class="seo-section-kicker">{{ $t('seo.imageModels.' + i18nKey + '.sectionKicker') }}</span>
          <h2 class="seo-section-title">{{ $t('brandSeo.sectionTitle', { title: $t('seo.imageModels.' + i18nKey + '.keyFeaturesTitle') }) }}</h2>
          <p>{{ $t('seo.imageModels.' + i18nKey + '.keyFeaturesIntro') }}</p>
        </div>
        <div class="seo-feature-grid">
          <article class="seo-feature-card" v-for="(feature, idx) in features" :key="idx">
            <span class="seo-feature-index">0{{ idx + 1 }}</span>
            <h3>{{ feature.name }}</h3>
            <p>{{ feature.short }}</p>
          </article>
        </div>
      </section>

      <section class="seo-section seo-showcase">
        <div class="seo-section-heading seo-section-heading--center">
          <span class="seo-section-kicker">{{ $t('seo.imageModels.' + i18nKey + '.showcaseKicker') }}</span>
          <h2 class="seo-section-title">{{ $t('seo.imageModels.' + i18nKey + '.showcaseTitle') }}</h2>
          <p>{{ $t('seo.imageModels.' + i18nKey + '.showcaseIntro') }}</p>
        </div>
        <article class="seo-showcase-row" :class="{ 'seo-showcase-row--text-only': !featureExamples(feature).length }" v-for="(feature, idx) in features" :key="'showcase-' + idx">
          <div class="seo-showcase-copy">
            <span>{{ idx + 1 }}</span>
            <h3>{{ feature.name }}</h3>
            <p>{{ feature.desc }}</p>
          </div>
          <div class="seo-example-list" v-if="featureExamples(feature).length">
            <div class="seo-example-pair" v-for="(example, exampleIdx) in featureExamples(feature)" :key="exampleIdx">
              <div class="seo-example-card">
                <span class="seo-example-label">{{ $t('seo.imageModels.demoPrompt') }}</span>
                <p>“{{ example.prompt }}”</p>
                <button type="button" @click="usePrompt(example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button>
              </div>
              <figure class="seo-example-visual">
                <img
                  v-if="example.image"
                  :src="example.image"
                  :alt="example.alt || feature.name + ' example'"
                  loading="lazy"
                >
                <div class="seo-image-placeholder" v-else>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  <span>{{ $t('seo.imageModels.imagePlaceholder') }}</span>
                  <small>{{ $t('seo.imageModels.replaceImageHint') }}</small>
                </div>
                <figcaption v-if="example.caption">{{ example.caption }}</figcaption>
              </figure>
            </div>
          </div>
        </article>
      </section>

      <section class="seo-section" v-if="variants.length">
        <div class="seo-section-heading">
          <span class="seo-section-kicker">{{ $t('seo.imageModels.' + i18nKey + '.variantsKicker') }}</span>
          <h2 class="seo-section-title">{{ $t('seo.imageModels.' + i18nKey + '.variantsTitle') }}</h2>
          <p>{{ $t('seo.imageModels.' + i18nKey + '.variantsIntro') }}</p>
        </div>
        <div class="seo-variant-grid">
          <article class="seo-variant-card" v-for="variant in variants" :key="variant.name">
            <div class="seo-variant-top">
              <h3>{{ variant.name }}</h3>
              <span>{{ variant.tag }}</span>
            </div>
            <p>{{ variant.desc }}</p>
            <strong>{{ variant.bestFor }}</strong>
          </article>
        </div>
      </section>

      <section class="seo-section seo-use-cases">
        <div class="seo-section-heading seo-section-heading--center">
          <span class="seo-section-kicker">{{ $t('seo.imageModels.' + i18nKey + '.useCasesKicker') }}</span>
          <h2 class="seo-section-title">{{ $t('seo.imageModels.' + i18nKey + '.useCasesTitle') }}</h2>
        </div>
        <div class="seo-use-case-grid">
          <article v-for="item in useCases" :key="item.name">
            <span class="seo-use-case-dot"></span>
            <h3>{{ item.name }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </section>

      <section class="seo-section seo-faq-section">
        <div class="seo-section-heading">
          <span class="seo-section-kicker">FAQ</span>
          <h2 class="seo-section-title">{{ $t('brandSeo.faqTitle', { title: $t('seo.imageModels.' + i18nKey + '.faqTitle') }) }}</h2>
        </div>
        <div class="seo-faqs">
          <details class="seo-faq" v-for="(faq, idx) in faqs" :key="idx">
            <summary><span>{{ faq.q }}</span><i></i></summary>
            <p>{{ faq.a }}</p>
          </details>
        </div>
      </section>

      <section class="seo-section seo-how-to">
        <div class="seo-how-to-heading">
          <span>{{ $t('seo.imageModels.' + i18nKey + '.howToKicker') }}</span>
          <h2>{{ $t('brandSeo.sectionTitle', { title: $t('seo.imageModels.' + i18nKey + '.howToTitle') }) }}</h2>
          <p>{{ $t('seo.imageModels.' + i18nKey + '.howToIntro') }}</p>
        </div>
        <div class="seo-how-to-grid">
          <article v-for="(step, idx) in howToSteps" :key="idx">
            <strong>0{{ idx + 1 }}</strong>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </article>
        </div>
        <button type="button" class="seo-how-to-btn" @click="scrollToTop">{{ $t('seo.imageModels.' + i18nKey + '.howToCta') }}</button>
      </section>

      <section class="seo-section seo-related-section">
        <h2 class="seo-section-title">{{ $t('seo.imageModels.relatedModelsTitle') }}</h2>
        <div class="seo-related">
          <router-link class="seo-related-card" v-for="relatedModel in relatedModels" :key="relatedModel.id" :to="modelLink(relatedModel.id)">
            <span class="seo-related-name">{{ relatedModel.name }}</span>
            <span class="seo-related-arrow">&rarr;</span>
          </router-link>
        </div>
      </section>
      </template>
    </div>
  </div>
</template>

<script>
var Flux1SeoContent = require('@/components/seo/Flux1SeoContent.vue').default
var SeedreamSeoContent = require('@/components/seo/SeedreamSeoContent.vue').default
var QwenImageSeoContent = require('@/components/seo/QwenImageSeoContent.vue').default
var GptImageSeoContent = require('@/components/seo/GptImageSeoContent.vue').default

// SEO slug 与模型配置映射；新增页面时在此登记。
var SLUG_MAP = {
  // FLUX.1 系列聚合页：默认使用 Pro 1.1 Ultra，并对比 Schnell
  'flux-1-ai-image-generator': { modelId: 'flux-pro-1-1-ultra', i18nKey: 'flux1' },
  'flux-2-ai-image-generator': { modelId: 'flux-2', i18nKey: 'flux2' },
  // Seedream 系列聚合页：以 Seedream 5 为主角，附带 Seedream 4 版本对比
  'seedream-ai-image-generator': { modelId: 'seedream-5', i18nKey: 'seedream' },
  // Qwen Image 2.0 Pro 单模型页（无版本对比）
  'qwen-image-2-0-pro-ai-image-generator': { modelId: 'qwen-image-2-0-pro', i18nKey: 'qwenImage2Pro' },
  // Stable Diffusion 3.5 单模型页（无版本对比）
  'stable-diffusion-ai-image-generator': { modelId: 'sd-3-5', i18nKey: 'stableDiffusion' },
  // Z Image Turbo 单模型页（无版本对比）
  'z-image-turbo-ai-image-generator': { modelId: 'z-image-turbo', i18nKey: 'zImageTurbo' },
  // Midjourney 单模型页（无版本对比）
  'midjourney-ai-image-generator': { modelId: 'midjourney', i18nKey: 'midjourney' },
  // GPT Image 系列聚合页：默认使用 GPT-Image-1.5，并对比 GPT-Image-1
  'gpt-image-ai-image-generator': { modelId: 'gpt-image-1-5', i18nKey: 'gptImage' }
}

module.exports = {
  name: 'ImageModelSeo',
  components: {
    Flux1SeoContent: Flux1SeoContent,
    SeedreamSeoContent: SeedreamSeoContent,
    QwenImageSeoContent: QwenImageSeoContent,
    GptImageSeoContent: GptImageSeoContent
  },
  data: function () {
    return { prompt: '' }
  },
  metaInfo: function () {
    var key = this.i18nKey ? ('seo.imageModels.' + this.i18nKey) : 'seo.imageModels'
    var title = this.$t(key + '.title')
    var desc = this.$t(key + '.description')
    var canonicalUrl = 'https://www.miralvis.com' + this.$route.path
    var faqEntities = this.faqs.map(function (faq) {
      return { '@type': 'Question', name: faq.q, acceptedAnswer: { '@type': 'Answer', text: faq.a } }
    })
    return {
      title: title,
      meta: [
        { vmid: 'description', name: 'description', content: desc },
        { vmid: 'og:title', property: 'og:title', content: title },
        { vmid: 'og:description', property: 'og:description', content: desc },
        { vmid: 'twitter:title', name: 'twitter:title', content: title },
        { vmid: 'twitter:description', name: 'twitter:description', content: desc }
      ],
      script: [
        { vmid: 'faq-jsonld', type: 'application/ld+json', json: { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqEntities } },
        { vmid: 'breadcrumb-jsonld', type: 'application/ld+json', json: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.miralvis.com/' },
          { '@type': 'ListItem', position: 2, name: 'AI Image Models', item: 'https://www.miralvis.com/models' },
          { '@type': 'ListItem', position: 3, name: this.modelName, item: canonicalUrl }
        ] } }
      ]
    }
  },
  computed: {
    slug: function () { return this.$route.params.slug || '' },
    config: function () { return SLUG_MAP[this.slug] || null },
    modelId: function () { return this.config ? this.config.modelId : '' },
    i18nKey: function () { return this.config ? this.config.i18nKey : '' },
    model: function () { return this.modelId ? this.$store.getters.getModelById(this.modelId) : null },
    modelName: function () { return this.model ? this.model.name : this.modelId },
    relatedModels: function () {
      var self = this
      return (this.$store.state.imageModels || []).filter(function (model) { return model.id !== self.modelId }).slice(0, 6)
    },
    features: function () { return this.translationArray('features') },
    // 合并模型专属问答与 Miral AI 免费试用说明，供页面和 FAQ 结构化数据共同使用。
    faqs: function () {
      var modelFaqs = this.translationArray('faqs')
      var brandFaqs = this.$t('brandSeo.imageFaqs')
      return modelFaqs.concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    badges: function () { return this.translationArray('badges') },
    variants: function () { return this.translationArray('variants') },
    useCases: function () { return this.translationArray('useCases') },
    howToSteps: function () { return this.translationArray('howToSteps') }
  },
  methods: {
    // 读取当前模型的数组型翻译配置，缺失时返回空数组。
    translationArray: function (field) {
      var key = 'seo.imageModels.' + this.i18nKey + '.' + field
      var value = this.$te(key) ? this.$t(key) : []
      return Array.isArray(value) ? value : []
    },
    // 构建保留当前语言前缀的模型链接。
    modelLink: function (modelId) {
      var lang = this.$route.params.lang
      var model = this.$store.getters.getModelById(modelId)
      var routeId = (model && model.routeId) ? model.routeId : modelId
      return (lang ? '/' + lang : '') + '/models/' + routeId
    },
    // 将输入的提示词带到实际生成页面。
    goGenerate: function () {
      if (!this.prompt.trim() || !this.modelId) return
      this.$router.push({ path: this.modelLink(this.modelId), query: { prompt: this.prompt.trim() } })
    },
    // 读取功能中的全部生成案例，并统一为提示词与图片配对的数据结构。
    featureExamples: function (feature) {
      if (feature.demo && feature.demo.rows && feature.demo.rows.length) return feature.demo.rows
      if (feature.prompt) return [{ prompt: feature.prompt, image: feature.image || '', alt: feature.alt || '' }]
      return []
    },
    // 将示例提示词填入首屏输入框并回到生成区域。
    usePrompt: function (prompt) {
      this.prompt = prompt
      this.scrollToTop()
    },
    // 平滑滚动至页面顶部的生成区域。
    scrollToTop: function () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.image-model-seo-page { padding: 0 0 88px; background: #fff; color: #17191f; }
.container { max-width: 1120px; margin: 0 auto; padding: 0 28px; }
.breadcrumb { display: flex; align-items: center; gap: 9px; margin-bottom: 30px; font-size: 13px; color: #a1a6b0; flex-wrap: wrap; }
.breadcrumb a { color: #707681; text-decoration: none; }
.breadcrumb a:hover { color: #059669; }
.seo-first-screen { display: flex; width: 100vw; box-sizing: border-box; margin-bottom: 92px; margin-left: calc(50% - 50vw); padding: 28px max(28px, calc((100vw - 1120px) / 2 + 28px)) 42px; flex-direction: column; background: radial-gradient(circle at 82% 8%, rgba(155, 231, 205, .62), transparent 30%), radial-gradient(circle at 16% 20%, rgba(213, 217, 255, .66), transparent 34%), linear-gradient(145deg, #f7f8fa, #f0f3f5); }
.seo-first-screen .breadcrumb { width: 100%; max-width: 1064px; margin: 0 auto 20px; }
.seo-hero { display: flex; width: 100%; max-width: 1064px; margin: 0 auto; flex: 1; flex-direction: column; align-items: center; justify-content: center; }
.seo-hero-copy { max-width: 900px; padding: 12px 0 0; text-align: center; }
.seo-section-kicker { display: inline-block; color: #059669; font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.seo-hero-title { max-width: 900px; margin: 14px auto 16px; font-size: clamp(38px, 5vw, 62px); line-height: 1.06; letter-spacing: -.045em; font-weight: 800; }
.seo-hero-intro { max-width: 780px; margin: 0 auto; color: #626873; font-size: 17px; line-height: 1.75; }
.seo-badges { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-top: 20px; }
.seo-badges span { padding: 8px 12px; border: 1px solid rgba(221, 225, 230, .9); border-radius: 999px; background: rgba(255, 255, 255, .68); color: #4f5560; font-size: 12px; font-weight: 600; }
.seo-prompt-card { width: 100%; box-sizing: border-box; margin-top: 32px; padding: 20px 22px 16px; border: 1px solid rgba(222, 226, 231, .92); border-radius: 20px; background: rgba(255, 255, 255, .92); box-shadow: 0 16px 50px rgba(26, 31, 41, .07); backdrop-filter: blur(12px); }
.seo-prompt-heading, .seo-prompt-footer, .seo-prompt-actions, .seo-variant-top { display: flex; align-items: center; justify-content: space-between; }
.seo-prompt-heading > div { display: flex; align-items: center; gap: 10px; }
.seo-prompt-label { color: #9399a3; font-size: 12px; }
.seo-prompt-heading strong { font-size: 13px; }
.seo-credit-pill { padding: 6px 10px; border-radius: 999px; background: #f0faf6; color: #047857; font-size: 11px; font-weight: 700; }
.seo-prompt-textarea { box-sizing: border-box; width: 100%; min-height: 112px; padding: 19px 0 8px; border: 0; outline: 0; resize: none; background: transparent; color: #20232a; font: 15px/1.65 inherit; }
.seo-prompt-textarea::placeholder { color: #b1b5bd; }
.seo-prompt-footer { padding-top: 12px; border-top: 1px solid #f0f1f3; }
.seo-prompt-hint, .seo-prompt-count { color: #adb1b8; font-size: 11px; }
.seo-prompt-actions { gap: 14px; }
.seo-generate { display: inline-flex; align-items: center; gap: 7px; padding: 10px 17px; border: 0; border-radius: 10px; background: #111318; color: #fff; cursor: pointer; font-size: 13px; font-weight: 700; transition: transform .18s, background .18s; }
.seo-generate:hover:not(:disabled) { transform: translateY(-1px); background: #059669; }
.seo-generate:disabled { background: #d8dbe0; cursor: not-allowed; }
.seo-section { margin-bottom: 100px; }
.seo-section-heading { max-width: 700px; margin-bottom: 34px; }
.seo-section-heading--center { margin-right: auto; margin-left: auto; text-align: center; }
.seo-section-title { margin: 9px 0 10px; color: #17191f; font-size: clamp(28px, 3vw, 38px); line-height: 1.2; letter-spacing: -.03em; }
.seo-section-heading > p { margin: 0; color: #727782; font-size: 15px; line-height: 1.7; }
.seo-feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.seo-feature-card { min-height: 160px; padding: 24px; border: 1px solid #e9eaed; border-radius: 16px; background: #fff; transition: transform .2s, box-shadow .2s, border-color .2s; }
.seo-feature-card:hover { transform: translateY(-3px); border-color: #cfe9df; box-shadow: 0 14px 36px rgba(26, 31, 41, .06); }
.seo-feature-index { color: #0a9b70; font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.seo-feature-card h3 { margin: 30px 0 8px; font-size: 17px; }
.seo-feature-card p { margin: 0; color: #747984; font-size: 13px; line-height: 1.65; }
.seo-showcase { padding: 72px; border-radius: 28px; background: #f7f8f9; }
.seo-showcase-row { display: grid; grid-template-columns: minmax(240px, .72fr) minmax(0, 1.28fr); gap: 48px; align-items: start; padding: 44px 0; border-top: 1px solid #e2e4e7; }
.seo-showcase-row--text-only { grid-template-columns: minmax(0, 720px); }
.seo-showcase-copy > span { color: #059669; font-size: 12px; font-weight: 800; }
.seo-showcase-copy h3 { margin: 8px 0 12px; font-size: 24px; letter-spacing: -.02em; }
.seo-showcase-copy p { margin: 0; color: #676d78; font-size: 14px; line-height: 1.8; }
.seo-example-list { display: flex; flex-direction: column; gap: 16px; }
.seo-example-pair { display: grid; grid-template-columns: minmax(0, 1fr) minmax(180px, 1fr); overflow: hidden; border: 1px solid #e3e5e8; border-radius: 15px; background: #fff; box-shadow: 0 8px 24px rgba(30, 34, 42, .04); }
.seo-example-card { display: flex; flex-direction: column; min-height: 190px; padding: 22px; background: #fff; }
.seo-example-label { color: #9a9fa8; font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.seo-example-card p { min-height: 66px; margin: 12px 0 18px; color: #3f444e; font-size: 13px; line-height: 1.7; }
.seo-example-card button { align-self: flex-start; margin-top: auto; padding: 0; border: 0; background: transparent; color: #059669; cursor: pointer; font-size: 12px; font-weight: 700; }
.seo-example-visual { aspect-ratio: 1 / 1; min-height: 190px; margin: 0; overflow: hidden; border-left: 1px solid #e8eaed; background: #f0f2f3; }
.seo-example-visual img { display: block; width: 100%; height: 100%; min-height: 190px; object-fit: cover; }
.seo-image-placeholder { display: flex; width: 100%; height: 100%; min-height: 190px; align-items: center; justify-content: center; flex-direction: column; gap: 8px; color: #9ba1aa; background: linear-gradient(145deg, #f5f6f7, #eceff1); text-align: center; }
.seo-image-placeholder span { color: #6f7580; font-size: 12px; font-weight: 700; }
.seo-image-placeholder small { max-width: 150px; color: #a3a8b0; font-size: 10px; line-height: 1.4; }
.seo-example-visual figcaption { padding: 9px 12px; color: #858b95; background: #fff; font-size: 10px; }
.seo-variant-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.seo-variant-card { padding: 24px; border: 1px solid #e7e9ec; border-radius: 15px; }
.seo-variant-top h3 { margin: 0; font-size: 18px; }
.seo-variant-top span { padding: 5px 8px; border-radius: 6px; background: #f2f4f5; color: #777d87; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.seo-variant-card p { min-height: 45px; margin: 14px 0; color: #737984; font-size: 13px; line-height: 1.65; }
.seo-variant-card > strong { color: #047857; font-size: 12px; }
.seo-use-cases { padding: 70px 60px; border: 1px solid #e8eaed; border-radius: 26px; }
.seo-use-case-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 34px 42px; }
.seo-use-case-dot { display: block; width: 8px; height: 8px; border-radius: 50%; background: #12a87b; box-shadow: 0 0 0 6px #eaf8f3; }
.seo-use-case-grid h3 { margin: 18px 0 8px; font-size: 16px; }
.seo-use-case-grid p { margin: 0; color: #777c86; font-size: 13px; line-height: 1.65; }
.seo-how-to { position: relative; overflow: hidden; padding: 72px 58px; border-radius: 28px; background: #15181d; color: #fff; }
.seo-how-to::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 14% 8%, rgba(110,231,183,.13), transparent 28%), radial-gradient(circle at 85% 85%, rgba(124,139,255,.12), transparent 34%), linear-gradient(145deg, rgba(255,255,255,.04), transparent 55%); pointer-events: none; }
.seo-how-to-heading, .seo-how-to-grid, .seo-how-to-btn { position: relative; z-index: 1; }
.seo-how-to-heading { max-width: 720px; margin: 0 auto 42px; text-align: center; }
.seo-how-to-heading > span { color: #6ee7b7; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.seo-how-to-heading h2 { margin: 12px 0 10px; color: #fff; font-size: clamp(28px, 3.5vw, 40px); letter-spacing: -.035em; }
.seo-how-to-heading p { margin: 0; color: #aeb4bd; font-size: 14px; line-height: 1.7; }
.seo-how-to-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.seo-how-to-grid article { display: flex; flex-direction: column; gap: 14px; min-height: 210px; }
.seo-how-to-grid article > strong { color: #fff; font-size: 52px; line-height: 1; letter-spacing: -.05em; }
.seo-how-to-grid article > div { flex: 1; padding: 24px; border: 1px solid rgba(255,255,255,.07); border-radius: 15px; background: rgba(8,10,14,.72); }
.seo-how-to-grid h3 { margin: 0 0 10px; color: #fff; font-size: 16px; }
.seo-how-to-grid p { margin: 0; color: #b5bac2; font-size: 13px; line-height: 1.7; }
.seo-how-to-btn { display: block; margin: 34px auto 0; padding: 13px 28px; border: 1px solid rgba(255,255,255,.22); border-radius: 999px; background: #fff; color: #17191f; cursor: pointer; font-size: 13px; font-weight: 800; transition: transform .18s, border-color .18s; }
.seo-how-to-btn:hover { transform: translateY(-2px); border-color: #6ee7b7; }
.seo-faq-section { max-width: 860px; margin-right: auto; margin-left: auto; }
.seo-faqs { border-top: 1px solid #e6e8eb; }
.seo-faq { border-bottom: 1px solid #e6e8eb; }
.seo-faq summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 2px; color: #24272d; cursor: pointer; font-size: 15px; font-weight: 650; list-style: none; }
.seo-faq summary::-webkit-details-marker { display: none; }
.seo-faq summary i { position: relative; width: 14px; height: 14px; flex: 0 0 14px; }
.seo-faq summary i::before, .seo-faq summary i::after { content: ''; position: absolute; top: 6px; left: 1px; width: 12px; height: 1px; background: #707681; }
.seo-faq summary i::after { transform: rotate(90deg); transition: transform .2s; }
.seo-faq[open] summary i::after { transform: rotate(0); }
.seo-faq p { max-width: 760px; margin: -4px 0 22px; color: #6f7580; font-size: 14px; line-height: 1.8; }
.seo-cta { margin-bottom: 90px; padding: 82px 28px; border-radius: 28px; background: #111318; color: #fff; text-align: center; }
.seo-cta > span { color: #6ee7b7; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.seo-cta h2 { margin: 14px 0 16px; color: #fff; font-size: clamp(29px, 3.4vw, 40px); letter-spacing: -.03em; }
.seo-cta p { max-width: 680px; margin: 0 auto 30px; color: #c4c8cf; font-size: 15px; line-height: 1.75; }
.seo-cta-btn { padding: 14px 30px; border: 0; border-radius: 11px; background: #fff; color: #16181d; cursor: pointer; font-size: 14px; font-weight: 800; transition: transform .18s; }
.seo-cta-btn:hover { transform: translateY(-2px); }
.seo-related-section { margin-bottom: 0; }
.seo-related-section .seo-section-title { font-size: 25px; }
.seo-related { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.seo-related-card { display: flex; align-items: center; justify-content: space-between; padding: 18px; border: 1px solid #e8eaed; border-radius: 12px; color: #20232a; text-decoration: none; transition: border-color .2s, transform .2s; }
.seo-related-card:hover { transform: translateY(-2px); border-color: #a8dcca; }
.seo-related-name { font-size: 13px; font-weight: 700; }
.seo-related-arrow { color: #059669; }
.image-model-seo-page--midjourney .seo-feature-grid { grid-template-columns: repeat(5, 1fr); gap: 10px; align-items: start; }
.image-model-seo-page--midjourney .seo-feature-card { min-height: 190px; padding: 22px 18px; border-radius: 22px; }
.image-model-seo-page--midjourney .seo-feature-card:nth-child(even) { margin-top: 26px; }
.image-model-seo-page--midjourney .seo-feature-card h3 { margin-top: 44px; }
.image-model-seo-page--midjourney .seo-showcase { padding: 76px 64px; }
.image-model-seo-page--midjourney .seo-showcase-row { grid-template-columns: minmax(220px, .64fr) minmax(0, 1.36fr); gap: 56px; align-items: center; }
.image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) { grid-template-columns: minmax(0, 1.36fr) minmax(220px, .64fr); }
.image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) .seo-showcase-copy { order: 2; }
.image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) .seo-example-list { order: 1; }
.image-model-seo-page--midjourney .seo-example-pair { grid-template-columns: minmax(180px, .72fr) minmax(0, 1.28fr); border-radius: 22px; box-shadow: 0 18px 42px rgba(45, 34, 50, .07); }
.image-model-seo-page--midjourney .seo-example-visual { aspect-ratio: 4 / 3; }
.image-model-seo-page--midjourney .seo-use-cases { padding: 66px; }
.image-model-seo-page--midjourney .seo-use-case-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
.image-model-seo-page--midjourney .seo-use-case-grid article { padding: 24px; border: 1px solid #e8eaed; border-radius: 16px; }
@media (max-width: 900px) {
  .seo-first-screen { padding-right: 28px; padding-left: 28px; }
  .seo-feature-grid { grid-template-columns: repeat(2, 1fr); }
  .seo-showcase { padding: 48px 32px; }
  .seo-showcase-row { gap: 30px; }
  .seo-example-pair { grid-template-columns: 1fr; }
  .seo-example-visual { border-top: 1px solid #e8eaed; border-left: 0; }
  .seo-use-case-grid { grid-template-columns: repeat(2, 1fr); }
  .seo-how-to-grid { grid-template-columns: 1fr; }
  .seo-how-to-grid article { min-height: 0; }
  .image-model-seo-page--midjourney .seo-feature-grid { grid-template-columns: repeat(2, 1fr); }
  .image-model-seo-page--midjourney .seo-feature-card:nth-child(even) { margin-top: 0; }
  .image-model-seo-page--midjourney .seo-showcase { padding: 52px 32px; }
  .image-model-seo-page--midjourney .seo-showcase-row { gap: 32px; }
  .image-model-seo-page--midjourney .seo-example-pair { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .container { padding: 0 18px; }
  .seo-first-screen { margin-bottom: 70px; padding: 20px 18px 28px; }
  .seo-first-screen .breadcrumb { margin-bottom: 8px; }
  .seo-hero { justify-content: flex-start; padding-top: 28px; }
  .seo-hero-copy { padding: 0; }
  .seo-hero-title { font-size: 38px; }
  .seo-hero-intro { font-size: 15px; }
  .seo-prompt-card { margin-top: 26px; }
  .seo-prompt-heading > div, .seo-prompt-footer { align-items: flex-start; }
  .seo-prompt-footer { gap: 16px; }
  .seo-prompt-hint { display: none; }
  .seo-prompt-actions { width: 100%; }
  .seo-generate { margin-left: auto; }
  .seo-section { margin-bottom: 72px; }
  .seo-feature-grid, .seo-variant-grid, .seo-use-case-grid, .seo-related { grid-template-columns: 1fr; }
  .seo-showcase { margin-right: -18px; margin-left: -18px; padding: 44px 18px; border-radius: 0; }
  .seo-showcase-row { grid-template-columns: 1fr; gap: 22px; }
  .seo-use-cases { padding: 46px 24px; }
  .seo-how-to { margin-right: -18px; margin-left: -18px; padding: 52px 18px; border-radius: 0; }
  .seo-how-to-grid article > strong { font-size: 42px; }
  .seo-cta { padding: 48px 20px; }
  .seo-cta h2 { font-size: 27px; }
  .image-model-seo-page--midjourney .seo-feature-grid,
  .image-model-seo-page--midjourney .seo-use-case-grid { grid-template-columns: 1fr; }
  .image-model-seo-page--midjourney .seo-showcase { padding: 44px 18px; }
  .image-model-seo-page--midjourney .seo-showcase-row,
  .image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) { grid-template-columns: 1fr; }
  .image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) .seo-showcase-copy,
  .image-model-seo-page--midjourney .seo-showcase-row:nth-of-type(even) .seo-example-list { order: initial; }
  .image-model-seo-page--midjourney .seo-use-cases { padding: 46px 20px; }
}
</style>
